import {Decoder} from '@raison/prelude';
import {
    AccountId,
    BaseCurrencyCode,
    CryptoAddress,
    CryptoCurrencyCode,
    DoubleString,
    FiatCurrencyCode,
    NonEmptyString,
    NonNegative,
    NonNegativeDoubleString,
    NonNegativeInt,
    PositiveInt,
    Source,
    TokenBlockchainCryptoCurrencyCode,
    TokenCurrencyCode,
} from '@raison/types';
import * as Sentry from '@sentry/react';
import BigNumber from 'bignumber.js';
import {pipe} from 'effect/Function';
import * as Record from 'effect/Record';
import {TypeOf, array, boolean, null as ionull, literal, number, record, string, type, union} from 'io-ts';
import {extendType, postprocess} from '~/library/io-ts-tools';

const CryptoAccountBase = type({
    id: AccountId,
    name: NonEmptyString,
    title: NonEmptyString,
    currency: CryptoCurrencyCode,
    decimals: NonNegativeInt,
    decimals_assets: NonNegativeInt,
    balance: DoubleString,
    balance_available: DoubleString,
    balance_locked: DoubleString,
    balance_in: DoubleString,
    balance_out: DoubleString,
    address: union([CryptoAddress, literal('')]),
    blockchain: type({
        currency: CryptoCurrencyCode,
        name: NonEmptyString,
        network_name: string,
    }),
    icon: union([Source, ionull]),
    provider_id: number,
});
const CryptoAccount = postprocess(CryptoAccountBase, a => ({
    ...a,
    balance: BigNumber.min(a.balance, a.balance_available).toString() as DoubleString,
}));

export const Fiat = type({
    id: AccountId,
    name: NonEmptyString,
    currency: FiatCurrencyCode,
    decimals: NonNegativeInt,
    decimals_assets: NonNegativeInt,
    balance: DoubleString,
    balance_available: DoubleString,
    balance_locked: DoubleString,
    balance_in: DoubleString,
    balance_out: DoubleString,
    icon: union([Source, ionull]),
    provider_id: number,
});

export const Token = type({
    id: AccountId,
    address: union([CryptoAddress, literal('')]),
    balance: DoubleString,
    balance_in: DoubleString,
    balance_out: DoubleString,
    blockchain: type({
        currency: TokenBlockchainCryptoCurrencyCode,
        name: NonEmptyString,
        network_name: string,
    }),
    currency: TokenCurrencyCode,
    decimals: NonNegativeInt,
    decimals_assets: NonNegativeInt,
    icon: union([Source, ionull]),
    minimal: union([NonNegativeDoubleString, ionull]),
    name: NonEmptyString,
    receive_enable: boolean,
    send_enable: boolean,
    status: string,
    symbol: TokenCurrencyCode,
    provider_id: number,
});

const TotalLax = record(BaseCurrencyCode, number);
const Total = record(BaseCurrencyCode, NonNegative);

const PublicRatesElem = type({
    ask: NonNegativeDoubleString,
    base_currency: BaseCurrencyCode,
    bid: NonNegativeDoubleString,
    id: PositiveInt,
    mid_price: NonNegativeDoubleString,
    pair: NonEmptyString,
    quote_currency: BaseCurrencyCode,
});

const Extras = type({
    name: NonEmptyString,
    value: NonEmptyString,
    description: union([string, ionull]),
});

export const MinimumDefault = record(BaseCurrencyCode, union([NonNegativeDoubleString, ionull, NonNegative]));
export type MinimumDefault = TypeOf<typeof MinimumDefault>;

const MinimumSendWithdrawLax = record(BaseCurrencyCode, number);
const MinimumSendWithdraw = record(BaseCurrencyCode, NonNegative);
export const PublicRates = array(PublicRatesElem);
export const CurrencyRates = record(BaseCurrencyCode, record(BaseCurrencyCode, number));

export const AssetsAccountsBase = type({
    fiat: array(Fiat),
    tokens: array(Token),
});
const AssetsAccounts = extendType(AssetsAccountsBase, {
    crypto: array(CryptoAccount),
});
export type AssetsAccounts = TypeOf<typeof AssetsAccounts>;

const MinimumsBase = type({
    default: MinimumDefault,
    withdraw: MinimumSendWithdraw,
});
const MinimumsLax = extendType(MinimumsBase, {
    send: MinimumSendWithdrawLax,
});
const Minimums = extendType(MinimumsBase, {
    send: MinimumSendWithdraw,
});

const AssetsBase = type({
    currencies: CurrencyRates,
    public_rates: PublicRates,
    extras: array(Extras),
    accounts: AssetsAccounts,
});
type AssetsLax = ({} & (typeof AssetsLax)['~standard']['types'])['output'];
export const AssetsLax = extendType(AssetsBase, {
    total: TotalLax,
    minimum: MinimumsLax,
});
export type Assets = ({} & (typeof Assets)['~standard']['types'])['output'];
export const Assets = extendType(AssetsBase, {
    total: Total,
    minimum: Minimums,
});

export function AssetsLaxToStrict(lax: AssetsLax): Assets {
    performLaxValidationCheck(lax);

    return {
        ...lax,
        total: pipe(
            lax.total,
            Record.map(n => Decoder.run(NonNegative, Math.max(0, n)).orThrow())
        ),
        minimum: {
            ...lax.minimum,
            send: pipe(
                lax.minimum.send,
                Record.map(n => Decoder.run(NonNegative, Math.max(0, n)).orThrow())
            ),
        },
        accounts: lax.accounts,
    };
}

export type TokenAccount = TypeOf<typeof Token>;
export type CurrencyRates = TypeOf<typeof CurrencyRates>;
export type Crypto = TypeOf<typeof CryptoAccount>;
export type Fiat = TypeOf<typeof Fiat>;
export type Token = TypeOf<typeof Token>;
export type PublicRates = TypeOf<typeof PublicRates>;

function performLaxValidationCheck(lax: AssetsLax) {
    const reports: string[] = [];
    function checkNumber(value: number, identifier: string) {
        if (value < 0) reports.push(`${value} AT ${identifier}`);
    }
    for (const [key, value] of Record.toEntries(lax.total)) {
        checkNumber(value, `total.${key}`);
    }
    for (const [key, value] of Record.toEntries(lax.minimum.send)) {
        checkNumber(value, `minimum.send.${key}`);
    }
    if (reports.length > 0) {
        Sentry.captureException(new Error(`Negative values in /v1/assets/default.\n${reports.join('\n')}`), {
            level: 'log',
        });
    }
}
